import { Form } from 'antd';
import { FormItemLayout } from 'antd/es/form/Form';
import { Validations } from 'interfaces';
import useFormValidation from 'utils/useFormValidation';
import { StyledAutoComplete } from './style';
import { Rule } from 'rc-field-form/lib/interface';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';

export interface AutoCompleteProps<
  OptionType extends DefaultOptionType | BaseOptionType = DefaultOptionType,
> extends Validations {
  label?: string;
  name?: (string | number)[] | string | number;
  layout?: FormItemLayout;
  initialValue?: string | string[] | number | number[];
  rules?: Rule[];
  onSearch?: ((value: string) => void) | undefined;
  placeholder?: React.ReactNode;
  options?: OptionType[] | undefined;
  size?: 'large' | 'middle' | 'small';
  /** Controlled usage (outside a form field). */
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option: OptionType) => void;
  onClear?: () => void;
  allowClear?: boolean;
  notFoundContent?: React.ReactNode;
  className?: string;
  popupClassName?: string;
  title?: string;
  noStyle?: boolean;
}

export const AutoComplete = <
  OptionType extends DefaultOptionType | BaseOptionType = DefaultOptionType,
>({
  label,
  name,
  layout,
  initialValue,
  validations,
  rules,
  onSearch,
  placeholder,
  options,
  size = 'large',
  value,
  onChange,
  onSelect,
  onClear,
  allowClear,
  notFoundContent,
  className,
  popupClassName,
  title,
  noStyle,
}: AutoCompleteProps<OptionType>) => {
  const { formValidations } = useFormValidation();
  const combinedRules = validations ? formValidations(validations) : rules;
  return (
    <Form.Item
      label={label}
      name={name}
      initialValue={initialValue}
      rules={combinedRules}
      layout={layout}
      noStyle={noStyle}
    >
      <StyledAutoComplete
        size={size}
        onSearch={onSearch}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={(changed) => onChange?.(changed as string)}
        onSelect={(selected, option) =>
          onSelect?.(selected as string, option as OptionType)
        }
        onClear={onClear}
        allowClear={allowClear}
        notFoundContent={notFoundContent}
        className={className}
        popupClassName={popupClassName}
        title={title}
      />
    </Form.Item>
  );
};
